import type { TOC } from '@ember/component/template-only';
import type { SafeString } from '@ember/template';
import { PhWarning, PhInfo, PhCheckCircle, PhLightbulb, PhXCircle } from 'ember-phosphor-icons';
import { get } from '@ember/object';

/**
 * Visual intent/severity options for the alert
 */
export type AlertBannerIntent = 'warning' | 'info' | 'success' | 'tip' | 'danger';

/**
 * Size options for the alert
 */
export type AlertBannerSize = 'sm' | 'md' | 'lg';

interface AlertBannerSignature {
  Args: {
    /**
     * Visual intent/severity of the alert
     * @default "warning"
     */
    intent?: AlertBannerIntent;
    /**
     * Title text for the alert (optional)
     */
    title?: string | SafeString;
    /**
     * Size of the alert
     * @default "md"
     */
    size?: AlertBannerSize;
    /**
     * Whether the banner is an actively-firing alert that should be
     * announced by assistive tech. Defaults to `true` (renders
     * `role="alert"`). Pass `@live={{false}}` for always-visible static
     * informational callouts so AT users don't hear an "alert" on page
     * load; the banner then renders as `role="region"` with no aria-live
     * semantics.
     *
     * **A11y note:** a `role="region"` landmark MUST have an accessible
     * name. When you pass `@live={{false}}`, you are responsible for
     * supplying one via splattributes — either `aria-label="…"` or
     * `aria-labelledby="<heading-id>"`. See
     * `tio-admin/app/templates/authenticated/settings/impersonation.gts`
     * for the labelledby-to-a-real-Heading pattern.
     * @default true
     */
    live?: boolean;
  };
  Blocks: {
    /**
     * Main content of the alert
     */
    default: [];
    /**
     * Optional action content (button, link)
     */
    action?: [];
  };
  Element: HTMLDivElement;
}

const ICON_SIZES = {
  sm: 16,
  md: 20,
  lg: 24,
};

/**
 * AlertBanner - Contextual alert/notice banner with icon and optional title
 *
 * Displays informational, warning, success, tip, or danger messages
 * with consistent styling. Supports 5 intents, 3 sizes, and an
 * optional action slot.
 *
 * Built on the `.app-alert*` semantic class family — the class owns
 * layout, sizing, and intent colors (routed through the TIO design
 * tokens), so this component only resolves the right modifier classes
 * and renders the right icon.
 *
 * @example Warning alert
 * ```gts
 * import AlertBanner from 'tio-ui/components/alert-banner';
 *
 * <AlertBanner @intent="warning" @title="Important Notice">
 *   Your loan servicer requires verification before processing.
 * </AlertBanner>
 * ```
 *
 * @example Info alert with action
 * ```gts
 * <AlertBanner @intent="info">
 *   You have 3 pending documents to review.
 *   <:action>
 *     <Button @intent="primary" @size="sm">Review Now</Button>
 *   </:action>
 * </AlertBanner>
 * ```
 *
 * @example Tip alert
 * ```gts
 * <AlertBanner @intent="tip" @title="Pro Tip">
 *   Completing your profile increases your match score by 25%.
 * </AlertBanner>
 * ```
 */
const AlertBanner: TOC<AlertBannerSignature> = <template>
  {{#let (if @intent @intent "warning") (if @size @size "md") as |intent size|}}
    {{#let (get ICON_SIZES size) as |iconSize|}}
      {{! `@live=false` swaps the live-region semantics off — used for
          always-visible informational callouts that shouldn't announce
          on page load. `role="region"` keeps the banner exposed as a
          named landmark while removing the implicit `aria-live="assertive"`
          from `role="alert"`.

          The consumer must supply the region's accessible name via
          splattributes (`aria-label` or `aria-labelledby`) when `@live`
          is false — a named region without a name is an a11y violation.
          We can't auto-wire `aria-labelledby` to `@title` here because
          TOCs can't allocate stable unique ids without a class backing;
          if a future caller needs that ergonomics, lift this to a class
          component first. }}
      <div
        data-test-alert-banner
        data-test-alert-intent={{intent}}
        class="app-alert app-alert--{{intent}} app-alert--{{size}}"
        role={{if (eq @live false) "region" "alert"}}
        ...attributes
      >
        <div class="app-alert-icon" data-test-alert-icon>
          {{#if (eq intent "warning")}}
            <PhWarning @size={{iconSize}} />
          {{else if (eq intent "info")}}
            <PhInfo @size={{iconSize}} />
          {{else if (eq intent "success")}}
            <PhCheckCircle @size={{iconSize}} />
          {{else if (eq intent "tip")}}
            <PhLightbulb @size={{iconSize}} />
          {{else if (eq intent "danger")}}
            <PhXCircle @size={{iconSize}} />
          {{/if}}
        </div>
        <div class="app-alert-content app-rich-text">
          {{#if @title}}
            <div data-test-alert-title class="app-alert-title">
              {{@title}}
            </div>
          {{/if}}
          <div data-test-alert-content class="app-alert-body">
            {{yield}}
          </div>
        </div>
        {{#if (has-block "action")}}
          <div data-test-alert-action class="app-alert-action">
            {{yield to="action"}}
          </div>
        {{/if}}
      </div>
    {{/let}}
  {{/let}}
</template>;

export default AlertBanner;
export type { AlertBannerSignature };
